import { useAtom } from 'jotai';
import React, { useMemo, useState } from 'react';
import { AdminSettingsSection, Checkbox, ColorPicker, Select } from 'src/components';
import ReactSwitch from 'react-switch';
import { CaretDown, CaretRight } from 'src/common/icons';
import { ACTION_TYPES } from 'src/shared/settings/base';
import { videoSettingsAtom } from 'src/shared/settings/video';

import styles from '../styles.module.scss';

const RESOLUTION_GROUPS = [
  { prefix: '360p', heading: 'Low-res (360p)' },
  { prefix: '480p', heading: 'Low-res (480p)' },
  { prefix: '720p', heading: 'High-res (720p)' },
  { prefix: '1080p', heading: 'HD (1080p)' },
  { prefix: 'hls', heading: 'Adaptive (variable bandwidth)' },
];

function groupAndOrderTranscodes(transcodes: any[]) {
  const groups: { heading: string; cards: any[] }[] = [];

  for (const { prefix, heading } of RESOLUTION_GROUPS) {
    const base = transcodes.find((t) => t.key === `${prefix}-video`);
    const wm = transcodes.find((t) => t.key === `${prefix}-wm-video`);
    const cards = [base, wm].filter(Boolean);
    if (cards.length > 0) {
      groups.push({ heading, cards });
    }
  }

  return groups;
}

type TranscodeCardProps = {
  video: any;
  onUpdate: (key: string, property: string, value: any) => void;
};

const TranscodeCard: React.FC<TranscodeCardProps> = ({ video, onUpdate }) => {
  const { key, watermark, padding, aspect, upscale, enabled, width, height, ...rest } = video;
  const [advancedOpen, setAdvancedOpen] = useState(false);

  return (
    <div className={styles.transcodeCard}>
      <div className={styles.transcodeCardHeader}>
        <div className={styles.transcodeCardTitle}>
          <label>Key</label>
          <span className={styles.keyPill}>{key}</span>
        </div>
        <ReactSwitch
          checked={enabled}
          onChange={(checked: boolean) => onUpdate(key, 'enabled', checked)}
          checkedIcon={false}
          uncheckedIcon={false}
          onColor="#2e53e3"
          width={40}
          height={20}
          handleDiameter={16}
        />
      </div>

      <div className={styles.transcodeCardBody}>
        <div className={styles.transcodeCardField}>
          <label>Size</label>
          <span className={styles.sizeBadge}>{width} x {height}</span>
        </div>
        <div className={styles.transcodeCardField}>
          <label>Watermarked</label>
          {key === 'hls-video' ? (
            <Checkbox
              checked={watermark}
              onChange={(e) => onUpdate(key, 'watermark', e.target.checked)}
            />
          ) : (
            <span className={`${styles.wmBadge} ${watermark ? styles.wmBadgeYes : styles.wmBadgeNo}`}>
              {watermark ? 'Yes' : 'No'}
            </span>
          )}
        </div>
        <div className={styles.transcodeCardField}>
          <label>Public</label>
          <Checkbox
            checked={rest.public}
            onChange={(e) => onUpdate(key, 'public', e.target.checked)}
          />
        </div>
      </div>

      <div
        className={styles.advancedToggle}
        onClick={() => setAdvancedOpen((prev) => !prev)}
      >
        {advancedOpen ? <CaretDown /> : <CaretRight />}
        <span>Advanced</span>
      </div>

      {advancedOpen && (
        <div className={styles.advancedContent}>
          <div className={styles.transcodeCardField}>
            <label>Mode</label>
            <Select
              value={aspect}
              onChange={(value) => onUpdate(key, 'aspect', value)}
              options={[
                { label: 'Pad', value: 'pad' },
                { label: 'Preserve', value: 'preserve' },
                { label: 'Scale', value: 'scale' },
                { label: 'Crop', value: 'crop' },
              ]}
            />
          </div>
          <div className={styles.transcodeCardField}>
            <label>Padding color</label>
            <ColorPicker value={padding} onChange={(color) => onUpdate(key, 'padding', color)} />
          </div>
          <div className={styles.transcodeCardField}>
            <label>Upscale</label>
            <Checkbox
              checked={upscale}
              onChange={(e) => onUpdate(key, 'upscale', e.target.checked)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export const VideoTranscodes: React.FC = () => {
  const [videoSettings, updateVideoSettings] = useAtom(videoSettingsAtom);

  const transcodes = useMemo(() => {
    if (!videoSettings) return [];
    return Object.entries(videoSettings.transcodes).map(([key, value]: [string, any]) => ({ key, ...value }));
  }, [videoSettings]);

  const groups = useMemo(() => groupAndOrderTranscodes(transcodes), [transcodes]);

  const handleVideoUpdate = (key: string, property: string, value: any) =>
    updateVideoSettings({
      type: ACTION_TYPES.UPDATE_STATE,
      payload: {
        transcodes: {
          [key]: {
            ...videoSettings.transcodes[key],
            [property]: value,
          },
        },
      },
    });

  return (
    <AdminSettingsSection title="Video transcodes">
      <p>Enable the transcode to create it when a video is uploaded</p>
      {groups.map((group) => (
        <div key={group.heading} className={styles.transcodeGroup}>
          <h5 className={styles.transcodeGroupHeading}>{group.heading}</h5>
          <div className={styles.transcodeCardGrid}>
            {group.cards.map((video) => (
              <TranscodeCard key={video.key} video={video} onUpdate={handleVideoUpdate} />
            ))}
          </div>
        </div>
      ))}
    </AdminSettingsSection>
  );
};
